import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ROUTES } from "../../routes/routerConfig";
import { cameFrom } from "../../utils/routeHistory";
import { PrimaryButton } from "../../components/ui/Button";
import {
  ALL_TICKET_STATUS_CODES,
  ApiTicketItem,
  CATEGORY_STYLE,
  STATUS_COLOR,
  TAB_TO_API_STATUS,
  Ticket,
} from "./support.types";
import { mapApiTicket } from "./supportMappers";
import service from "../../services/api/service";
import { decryptObject, encryptObject } from "../../utils/crypto";
import { useAuth } from "../../hooks/useAuth";
import {
  qrcFlowTypeSelector,
  qrcLanOptionsSelector,
  qrcAppIdsSelector,
  qrcEmailSelector,
} from "../../services/redux/slices/authSlice";
import { useQrcAppIds, useQrcIdentifiers, useQrcIdentifiersSettled } from "./qrcIdentifiers";
import Card from "../../components/ui/Card";
import Breadcrumbs from "../../components/ui/Breadcrumbs";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Trims the time off a formatted date. A blank value means the CRM had no date at all (see
// formatApiDate), so it shows a dash rather than leaving an empty gap under the label.
const dateOnly = (value: string): string => value.split(",")[0]?.trim() || "-";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  "All",
  "In-Progress",
  "Pending Information",
  "Resolved",
  "Closed",
] as const;

// Sent as-is for the "All" tab, where every known ticket status should be included.
const ALL_STATUS_CODES = ALL_TICKET_STATUS_CODES;

const ALL_LAN_VALUE = "__ALL__";

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: Ticket["status"] }> = ({ status }) => {
  const color = STATUS_COLOR[status];
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: 1,
        border: `1.5px solid ${color}`,
        color,
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        lineHeight: 1.5,
      }}
    >
      {status}
    </Box>
  );
};


// ─── TicketCard Skeleton ──────────────────────────────────────────────────────

const TicketCardSkeleton: React.FC = () => (
  // Same flat bordered frame as TicketCard, so the list doesn't shift when data arrives.
  <Box
    sx={{
      bgcolor: "#fff",
      border: "1px solid #E8EAED",
      borderRadius: "10px",
      mb: "10px",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Box sx={{ flex: 1 }}>
        <Skeleton width={120} height={18} sx={{ mb: 0.75 }} />
        <Skeleton width="60%" height={28} />
      </Box>
      <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, ml: 2 }} />
    </Box>

    <Divider sx={{ borderColor: "#f0f0f0", mx: { xs: 2.5, sm: 3 } }} />

    <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2, pb: 2.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: { xs: 2, sm: 0 }, mb: 2 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i}>
            <Skeleton width="50%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton width="70%" height={20} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Skeleton width={60} height={16} sx={{ mb: 0.5 }} />
          <Skeleton width={90} height={20} />
        </Box>
        <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: 2 }} />
      </Box>
    </Box>
  </Box>
);

// ─── TicketCard ───────────────────────────────────────────────────────────────

const TicketCard: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const catStyle = CATEGORY_STYLE[ticket.category];
  // Same breakpoint the rest of the app treats as "mobile" (see Header, RaiseARequest), so the
  // card's three mobile adjustments below all flip at one place rather than at two.
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCopy = () => {
    navigator.clipboard.writeText(ticket.ticketNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const handleViewDetails = () => {
    navigate(
      `${ROUTES.ContactUs.ticketDetails.locationPath}/${ticket.ticketNumber}`,
      { state: { ticket, chatMode: !!ticket.loanAccountNumber } }
    );
  };

  // Built once and placed per breakpoint below: beside the label on desktop, on its own line
  // under the title on mobile. Note the space after "ID:" — the number ran into it before.
  const escalatedChip = ticket.escalatedFrom ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        border: "1.5px solid #00B3B5",
        borderRadius: 1,
        color: "#00B3B5",
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.5,
      }}
    >
      <LocalOfferOutlinedIcon sx={{ fontSize: 13 }} />
      Escalated against ticket ID: {ticket.escalatedFrom}
    </Box>
  ) : null;

  // Rendered in two places, one per breakpoint: on mobile it joins the meta grid so it pairs
  // with Created Date, on sm+ it stays on the button's row as the design has it.
  const dueDateBlock = (
    <>
      <Typography className="inside-page-row-lable" sx={{ mb: 0.5, display: "block" }}>
        Due Date
      </Typography>
      <Typography className="inside-page-row-value">
        {dateOnly(ticket.dueDate)}
      </Typography>
    </>
  );

  return (
    <>
    {/* Flat bordered card, per the design: 10px radius + a light hairline instead of the shared
        Card's drop shadow. Padding lives on the sections below, so this wrapper adds none. */}
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E8EAED",
        borderRadius: "10px",
        mb: "10px",
        overflow: "hidden",
      }}
    >
      {/* Top section: label + ref chip + category chip + status */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 2,
          pb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* Row 1: the "Type of Ticket" label, with the escalation chip beside it on sm+,
              where there is room for both on one line. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
            <Typography className="loan-account-sub-heading">Type of Ticket</Typography>
            {!isXsOrSm && escalatedChip}
          </Box>

          {/* Row 2: subject — rendered only when present, otherwise the empty heading still took
              its line height plus margin and left ~30px of dead space under the label. */}
          {ticket.typeOfTicket && (
            <Typography className="loan-account-main-heading">{ticket.typeOfTicket}</Typography>
          )}

          {/* Mobile: the chip gets its own line under the title. On the top row it squeezed the
              status badge and crowded the label, so the two competed for the first line. Below
              the title it reads as a note about the ticket, and the label/title pair stays the
              first thing scanned. */}
          {isXsOrSm && escalatedChip && <Box sx={{ mt: 1 }}>{escalatedChip}</Box>}
        </Box>

        <StatusBadge status={ticket.status} />
      </Box>

      {/* Inset to the sections' padding, as in the design, rather than running edge to edge */}
      <Divider sx={{ borderColor: "#f0f0f0", mx: { xs: 2.5, sm: 3 } }} />

      {/* Meta section */}
      <div className="view-tickets-container">
      <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 1.5, pb: 2 }} >
        {/* Three-column meta row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
            gap: { xs: 1.5, sm: 0 },
            mb: 1.25,
          }}
        >
          <Box>
            <Typography className="inside-page-row-lable" sx={{ mb: 0.5, display: "block" }}>
              Category
            </Typography>
            <Typography className="inside-page-row-value">
              {ticket.subCategory ?? ticket.category}
            </Typography>
          </Box>

          <Box>
            <Typography className="inside-page-row-lable" sx={{ mb: 0.5, display: "block" }}>
              Ticket Number
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography className="inside-page-row-value">
                {ticket.ticketNumber}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box>
            <Typography className="inside-page-row-lable" sx={{ mb: 0.5, display: "block" }}>
              Created Date
            </Typography>
            <Typography className="inside-page-row-value">
              {dateOnly(ticket.dateTime)}
            </Typography>
          </Box>

          {/* Mobile only: the grid is two columns on xs, so with three items Created Date sat
              alone with an empty cell beside it. Due Date fills that cell instead. */}
          {isXsOrSm && <Box>{dueDateBlock}</Box>}
        </Box>

        {/* Due date + View Details row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isXsOrSm ? "stretch" : "flex-end",
          }}
        >
          {/* sm+ only — on mobile this now lives in the grid above */}
          {!isXsOrSm && <Box>{dueDateBlock}</Box>}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              // Full-width button on mobile: on a narrow card a right-aligned pill left a wide
              // dead gap to its left, and it now reads as the card's primary action.
              width: isXsOrSm ? "100%" : "auto",
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleViewDetails}
              sx={{
                bgcolor: "#00B3B5",
                color: "#fff",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                width: isXsOrSm ? "100%" : "auto",
                boxShadow: "none",
                "&:hover": { bgcolor: "#009799", boxShadow: "none" },
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Box>
      </div>

      {/* Escalated ticket notice — full-width cream banner at the bottom of the card */}
      {ticket.escalatedTo && (
        <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              bgcolor: "#FCF6E3",
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#EAB308", fontSize: 20, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.875rem", color: "#3A3A3A", lineHeight: 1.5 }}>
              This ticket has been closed and new complaint ticket has been raised with ticket ID: {ticket.escalatedTo}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
    </>
  );
};

// ─── ViewTickets ──────────────────────────────────────────────────────────────

const TICKETS_PER_PAGE = 5;

// Opening a ticket unmounts this listing, so the page/tab/LAN/sort is kept in sessionStorage and
// read back on mount — but only when the user is returning from a ticket's details page. Arriving
// from Lodge a Complaint, the sidebar or anywhere else starts clean on page 1 / "All".
//
// The "came from" check is what scopes it. An earlier attempt consumed the key on read instead,
// which broke under StrictMode: dev mounts this component twice, the first mount ate the key and
// the second restored nothing.
const LIST_VIEW_KEY = "viewTickets:view";

interface ListView {
  page: number;
  activeTab: string;
  selectedLan: string;
  sortBy: string;
}

const writeListView = (view: ListView) => {
  try {
    sessionStorage.setItem(LIST_VIEW_KEY, JSON.stringify(view));
  } catch {
    // Private-mode / quota failures just mean the view isn't remembered.
  }
};

const readListViewForReturnTrip = (): Partial<ListView> => {
  try {
    if (!cameFrom(ROUTES.ContactUs.ticketDetails.locationPath)) return {};
    const saved = sessionStorage.getItem(LIST_VIEW_KEY);
    if (!saved) return {};
    const view = JSON.parse(saved) as Partial<ListView>;
    // Sanitize: an unknown tab value would leave the Tabs strip with nothing selected.
    return {
      page: typeof view.page === "number" && view.page > 0 ? view.page : undefined,
      activeTab: (STATUS_TABS as readonly string[]).includes(view.activeTab ?? "")
        ? view.activeTab
        : undefined,
      selectedLan: typeof view.selectedLan === "string" ? view.selectedLan : undefined,
      sortBy: view.sortBy === "recent" || view.sortBy === "oldest" ? view.sortBy : undefined,
    };
  } catch {
    return {};
  }
};

const ViewTickets: React.FC = () => {
  const navigate = useNavigate();
  const qrcLanOptions: string[] = useSelector(qrcLanOptionsSelector) ?? [];
  // Post-login the same qrc/user-verification look-up the pre-login journey runs, so an
  // application that has a CRM id but no LAN yet is listed here too (empty pre-login, where
  // qrcLanOptions already holds it).
  const verifiedIdentifiers = useQrcIdentifiers();
  const identifiersSettled = useQrcIdentifiersSettled();
  // appIds from qrc/user-verification, sent as applicationId below. Pre-login they are stored at
  // OTP time (HelpCenter); post-login they come from the same look-up as the identifiers.
  const qrcAppIds: string[] = useSelector(qrcAppIdsSelector) ?? [];
  const verifiedAppIds = useQrcAppIds();
  const qrcFlowType = useSelector(qrcFlowTypeSelector);
  const qrcEmail = useSelector(qrcEmailSelector);

  const [refreshing, setRefreshing] = useState(false);
  const { isAuthenticated } = useAuth();
  // A logged-in session always wins over stale QRC state left in this tab by an earlier
  // pre-login journey — otherwise the pre-login treatment leaks onto post-login screens.
  const isQrcFlow = !!qrcFlowType && !isAuthenticated;
  const isNonBorrowerFlow = qrcFlowType === "nonBorrower" && !isAuthenticated;

  // False until the LAN/application options are final. The first listing request fires before the
  // look-up resolves, so it filters on a partial LAN set and the API reports a smaller
  // totalPages — enough to make the out-of-range guard below discard a restored page.
  const lanOptionsReadyRef = useRef(false);

  useEffect(() => {
    // qrc/user-verification is the only async source of this page's identifiers, so its finishing
    // is what makes the options final — whether or not it returned any.
    if (identifiersSettled) lanOptionsReadyRef.current = true;
  }, [identifiersSettled]);

  const loanOptions = [
    { value: ALL_LAN_VALUE, label: "All", type: "all" as const },
    // A pre-login QRC visitor sees only what their own verification returned, never the
    // persisted option slice, which can still hold the previous logged-in user's loan accounts.
    ...(isQrcFlow
      ? qrcLanOptions.map((lan) => ({ value: lan, label: lan, type: "accountNumber" as const }))
      : []),
    // Post-login lists exactly what qrc/user-verification returns — the same set, in the same
    // order, as the pre-login journey shows for the same mobile. The portal's own accounts are
    // the fallback for when that look-up comes back empty (no match, or the call failed), so the
    // filter is never left with nothing to pick.
    // qrc/user-verification returns both buckets — disbursed loans and in-flight applications
    // (see extractLanList) — so it is the whole list. The portal's own user-application/fetch
    // used to stand behind it, but it only ever repeated the application half of the same data,
    // and was requested on every visit to fill a gap that no longer exists.
    ...(isQrcFlow
      ? []
      : verifiedIdentifiers.map((lan) => ({ value: lan, label: lan, type: "accountNumber" as const }))),
  ].filter((opt, index, all) => all.findIndex((o) => o.value === opt.value) === index);
  // Content, not just count: the fallback above swaps one set of LANs for another, and those two
  // sets can happen to be the same length — which a length-keyed effect would not notice.
  const loanOptionsKey = loanOptions.map((o) => o.value).join(",");
  const appIds = isQrcFlow ? qrcAppIds : verifiedAppIds;
  const appIdsKey = appIds.join(",");

  // Read once on mount so a re-render never re-applies a stale view.
  const savedView = useRef(readListViewForReturnTrip()).current;
  const [selectedLan, setSelectedLan] = useState<string>(savedView.selectedLan ?? ALL_LAN_VALUE);
  const [activeTab, setActiveTab] = useState<string>(savedView.activeTab ?? "All");
  const [sortBy, setSortBy] = useState(savedView.sortBy ?? "recent");
  const [page, setPage] = useState(savedView.page ?? 1);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchTickets = useCallback(async (currentPage: number, status: string, lan: string, allLans: string[], applicationIds: string[] = [], refresh = false) => {
    const isAllTab = status === "All";
    const isAllLan = lan === ALL_LAN_VALUE;
    const lanValues = isAllLan ? allLans : lan ? [lan] : [];
    // Non-borrower ("No, I don't") flow: there's no LAN to filter by, so this listing is
    // keyed on the verified email instead. Every other flow keeps the existing LAN-based call.
    if (!isNonBorrowerFlow && lanValues.length === 0) return;
    if (isNonBorrowerFlow && !qrcEmail) return;
    setLoading(true);
    // `refresh` is only true on an explicit Refresh-button click; it drives the button spinner.
    if (refresh) setRefreshing(true);
    try {
      const apiStatus = TAB_TO_API_STATUS[status];
      const statusValues = isAllTab ? ALL_STATUS_CODES : apiStatus;
      // A ticket raised against an application is matched on its appId, which has no LAN yet, so
      // the appIds ride along in the same lan array. Only while the filter is "All": once a
      // single row is picked, the listing must show that one identifier's tickets and nothing
      // else.
      const lanPayloadValues = isAllLan ? [...lanValues, ...applicationIds] : lanValues;
      const listingPayload = {
        ...(isNonBorrowerFlow ? { email: qrcEmail } : { lan: lanPayloadValues }),
        ...(statusValues !== undefined && { status: statusValues }),
      };
      const encryptedData = encryptObject(listingPayload);

      // `refresh` is passed as a query param (?...&refresh=true) only on a Refresh-button click.
      const res = await service.support.getTicketListing(
        { data: encryptedData },
        currentPage,
        TICKETS_PER_PAGE,
        refresh
      );
      const payload = res?.data;          // { status, message, data, pagination } — "data" is sometimes
      const rawData = payload?.data;      // the encrypted string directly, sometimes { data, pagination } again
      const encryptedTickets = typeof rawData === "string" ? rawData : rawData?.data;
      const paginationInfo = payload?.pagination ?? rawData?.pagination;
      const apiTickets: ApiTicketItem[] = decryptObject(encryptedTickets) ?? [];
      setTickets(apiTickets.map(mapApiTicket));
      const pages = paginationInfo?.totalPages ?? 1;
      setTotalPages(pages);
      // A restored page can sit past the end of the list (tickets closed or moved tab since it
      // was saved) — drop to the last real page, which re-runs this fetch for that page. Skipped
      // until the LAN options are final: a partial LAN filter under-reports totalPages, and
      // clamping on that threw away the page the user actually came back to.
      if (lanOptionsReadyRef.current && currentPage > pages) setPage(pages);
    } catch {
      setTickets([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      if (refresh) setRefreshing(false);
    }
  }, [isNonBorrowerFlow, qrcEmail]);

  // Guards against re-requesting an identical listing. This effect re-runs whenever the LAN
  // options grow — they arrive from two async sources (redux + the applications API) — and React's
  // StrictMode double-invokes effects in development, so the same page/tab/LAN combination was
  // being fetched three times per load. Only a change that alters the request fires a new one.
  const lastRequestKeyRef = useRef("");

  useEffect(() => {
    const allLans = loanOptions.filter((o) => o.value !== ALL_LAN_VALUE).map((o) => o.value);
    const requestKey = [page, activeTab, selectedLan, allLans.join(","), appIdsKey].join("|");
    if (requestKey === lastRequestKeyRef.current) return;
    lastRequestKeyRef.current = requestKey;
    fetchTickets(page, activeTab, selectedLan, allLans, appIds);
    // keyed on the values, not the array identity
  }, [page, activeTab, selectedLan, fetchTickets, loanOptionsKey, appIdsKey]);

  // Kept current so it's ready whenever the user opens a ticket. Safe to leave behind: it's only
  // read back when the previous route was a ticket details page.
  useEffect(() => {
    writeListView({ page, activeTab, selectedLan, sortBy });
  }, [page, activeTab, selectedLan, sortBy]);

  // A restored LAN can belong to an earlier session in this tab (e.g. QRC re-verified with a
  // different account) — once the options have loaded, fall back to "All" if it's gone.
  useEffect(() => {
    if (selectedLan === ALL_LAN_VALUE) return;
    if (loanOptions.length <= 1) return; // options still loading
    if (!loanOptions.some((o) => o.value === selectedLan)) {
      setSelectedLan(ALL_LAN_VALUE);
      setPage(1);
    }
  }, [selectedLan, loanOptionsKey]); // keyed on the values, not the array identity (as above)

  const handleTabChange = (tab: string) => { setActiveTab(tab); setPage(1); };
  const handleLanChange = (lan: string) => { setSelectedLan(lan); setPage(1); };
  // The list is paginated newest-first, so "Oldest" lives on the last page — jump there;
  // "Recent" starts from page 1.
  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(value === "oldest" ? totalPages : 1);
  };

  // Re-fetch the current listing (same page/tab/LAN filters) with refresh=true in the payload.
  const handleRefresh = () => {
    const allLans = loanOptions.filter((o) => o.value !== ALL_LAN_VALUE).map((o) => o.value);
    fetchTickets(page, activeTab, selectedLan, allLans, appIds, true);
  };

  const sortedTickets = [...tickets].sort((a, b) => {
    const aTime = a.raisedOnIso ? new Date(a.raisedOnIso).getTime() : 0;
    const bTime = b.raisedOnIso ? new Date(b.raisedOnIso).getTime() : 0;
    return sortBy === "oldest" ? aTime - bTime : bTime - aTime;
  });

  const breadcrumbItems = [
    { label: "Dashboard", path: "/" },
    { label: "Contact Us" },
  ];

  return (
    <Box>
      {/* {!isQrcFlow && <Breadcrumbs items={breadcrumbItems} />} */}
      {/* Page title — omitted in the QRC hub, where the active "View Existing Tickets" tab
          directly above already labels the panel and the heading only cost vertical space. */}
      {!isQrcFlow && (
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ color: "#168794", fontSize: "15px", fontWeight: "600", marginBottom: "20px" }}
            >
              Existing Tickets
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* List toolbar — status pills plus refresh / LAN / sort, grouped into one bordered strip so
          they read as controls for the list below rather than sharing a line with the page-level
          tabs above. Wraps onto a second line when the column is too narrow for both; forcing
          nowrap used to squeeze the pills into an arrow scroller instead. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          px: { xs: 1, sm: 1.5 },
          py: 0.75,
          borderRadius: "10px",
          bgcolor: "#fff",
          border: "1px solid #EFF1F3",
          flexWrap: "wrap",
        }}
      >
        {/* Pill filter tabs — basis wide enough for all five pills so they claim the row and the
            refresh / LAN / sort controls wrap below rather than compressing them. */}
        <Box
  sx={{
    flex: "1 1 420px",
    minWidth: 0,
    bgcolor: "transparent",
    "& .MuiTabs-scrollButtons": {
      color: "#00B3B5",
      "&.Mui-disabled": { opacity: 0.3 }
    }
  }}
>
  <Tabs
    value={activeTab}
    onChange={(event, newValue) => handleTabChange(newValue)}
    variant="scrollable"
    scrollButtons="auto" // Automatically shows arrows on mobile/overflow, hides them when everything fits
    allowScrollButtonsMobile // Forces arrows to appear on mobile screens
    aria-label="status filter tabs"
    sx={{
      minHeight: "auto",
      "& .MuiTabs-flexContainer": {
        gap: 1, // Keeps the spacing between your pill items
      },
      "& .MuiTabs-indicator": {
        display: "none", // Hides the standard bottom underline indicator
      },
    }}
  >
    {STATUS_TABS.map((tab) => {
      const isActive = activeTab === tab;
      return (
        <Tab
          key={tab}
          value={tab}
          label={tab}
          disableRipple
          sx={{
            borderRadius: 50,
            border: `1.5px solid ${isActive ? "#00B3B5" : "#D1D5DB"}`,
            color: isActive ? "#00B3B5 !important" : "#6B7280 !important",
            bgcolor: "#fff",
            textTransform: "none",
            fontWeight: isActive ? 600 : 400,
            // Trimmed so all five pills and the sort controls share one toolbar line — the
            // two-line toolbar was costing ~70px of the list's visible height.
            fontSize: "0.8125rem",
            px: 1.5,
            py: 0.4,
            minWidth: "auto",
            minHeight: "auto",
            lineHeight: 1.5,
            opacity: 1, // Overrides MUI's default unselected opacity drop
            "&:hover": {
              bgcolor: isActive ? "rgba(0,179,181,0.04)" : "#f9fafb",
              borderColor: isActive ? "#00B3B5" : "#9CA3AF",
            },
          }}
        />
      );
    })}
  </Tabs>
</Box>

        {/* Refresh + LAN dropdown + Sort — right-aligned in the toolbar, or its own line when the
            pills have taken the first one. */}
        <Box sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexShrink: 0,
          flexWrap: "nowrap",
          ml: { md: "auto" },
          width: { xs: "100%", md: "auto" },
        }}>
          <Tooltip title="Refresh list">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing || loading}
                aria-label="Refresh list"
                size="small"
                sx={{
                  flexShrink: 0,
                  // Height matched to the status pills and the two selects beside it.
                  width: 32,
                  height: 32,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1.5,
                  color: "#00B3B5",
                  "&:hover": { borderColor: "#00B3B5", bgcolor: "rgba(0,179,181,0.04)" },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={18} sx={{ color: "#00B3B5" }} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Select
            value={selectedLan}
            onChange={(e) => handleLanChange(e.target.value)}
            size="small"
            displayEmpty
            renderValue={(v) => {
              const selected = loanOptions.find((o) => o.value === v);
              if (v === ALL_LAN_VALUE) {
                return (
                  <Typography variant="body2" color="text.primary" noWrap fontWeight={700}>
                    All
                  </Typography>
                );
              }
              return v ? (
                <Typography variant="body2" color="text.primary" noWrap>
                  {selected?.type === "accountNumber" ? "Loan A/c" : "App No."}:{" "}
                  <Box component="span" fontWeight={700}>{v}</Box>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" noWrap>
                  Loan Account / Application No.
                </Typography>
              );
            }}
            sx={{
              borderRadius: 1.5,
              flex: { xs: 1, md: "none" },
              // Narrower so the toolbar stays on one line inside the hub card.
              minWidth: { xs: 0, md: 150 },
              maxWidth: { xs: "none", md: 200 },
              height: 32,
              fontSize: "0.8125rem",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              "& .MuiSelect-select": { py: 0, pl: 1.5, display: "flex", alignItems: "center" },
            }}
          >
            {loanOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.type === "all" ? (
                  <Box component="span" fontWeight={600}>{opt.label}</Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        px: 0.75,
                        py: 0.2,
                        borderRadius: 0.75,
                        bgcolor: opt.type === "accountNumber" ? "#e6f7f7" : "#f0f0ff",
                        color: opt.type === "accountNumber" ? "#00B3B5" : "#6366f1",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opt.type === "accountNumber" ? "Loan A/c" : "App No."}
                    </Box>
                    {opt.label}
                  </Box>
                )}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            size="small"
            renderValue={(v) => (
              <Typography variant="body2" color="text.primary" noWrap>
                Sort by:{" "}
                <Box component="span" fontWeight={700}>
                  {v === "oldest" ? "Oldest" : "Recent"}
                </Box>
              </Typography>
            )}
            sx={{
              borderRadius: 1.5,
              flexShrink: 0,
              minWidth: { xs: 110, md: 140 },
              height: 32,
              fontSize: "0.8125rem",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              "& .MuiSelect-select": { py: 0, pl: 1.5, display: "flex", alignItems: "center" },
            }}
          >
            <MenuItem value="recent">Recent</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Ticket list or empty state */}
      {loading ? (
        <>
          {Array.from({ length: TICKETS_PER_PAGE }).map((_, i) => (
            <TicketCardSkeleton key={i} />
          ))}
        </>
      ) : tickets.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
              No tickets found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try a different filter or raise a new support request.
            </Typography>
            <PrimaryButton onClick={() => navigate(ROUTES.ContactUs.raiseRequest.locationPath)}>
              Raise a Request
            </PrimaryButton>
          </Box>
        </Card>
      ) : (
        <>
          {/* No scroll container here: in the QRC hub the shell card owns the single scroll region
              (see ContactUsHub), and nesting a second one gave two scrollbars side by side. */}
          {sortedTickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} />)}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, val) => setPage(val)}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#6B7280",
                    borderColor: "#e0e0e0",
                    fontWeight: 500,
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    bgcolor: "#00B3B5",
                    color: "#fff",
                    borderColor: "#00B3B5",
                    "&:hover": { bgcolor: "#009799" },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ViewTickets;